<template>
  <div>
    <!-- Header -->
    <div class="mb-6">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 class="text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight text-gray-900">
            Bonjour {{ storeName }} 👋
          </h1>
          <p class="text-sm text-gray-500 mt-0.5">{{ $d(new Date(), "fullDate") }}</p>
        </div>
        <div class="flex gap-2 self-start">
          <SecondaryButton class="whitespace-nowrap text-lg" @click="$router.push('/pos/dashboard')">
            📊 Pilotage
          </SecondaryButton>
          <SecondaryButton
            v-if="allowedToAdjustMonthTarget"
            class="whitespace-nowrap text-lg"
            @click="editTargetModalOpen = true"
          >
            🎯 Ajuster l'objectif
          </SecondaryButton>
        </div>
      </div>
    </div>

    <!-- Follows the selected store, like the training callout, so it clears when the selection is cleared -->
    <ChallengeHomeBlock :store-id="selectedStoreId" class="mb-6" />

    <BriefBar
      v-if="roadmapPerformance"
      :dailyTarget="dailyTarget"
      :yesterdayPerformance="yesterdayPerformance"
      :monthlyPerformance="monthlyPerformance"
      :leadsWaitingForConfirmation="leadsWaitingForConfirmation"
      :boostEnabled="retributionsBoostEnabled()"
      class="mb-6"
    />
    <Loading v-else-if="selectedStoreLocalId" />

    <!-- Callouts: rendered once the training modules are known, so the row appears directly in its final layout.
         A wide slot (training, or support without training) and a narrow one (subscription, or support when the
         store sells through an external POS only); support alone takes the whole row. -->
    <div v-if="trainingModulesStatus !== 'loading'" class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <QuizCallout v-if="firstModule" :training-module="firstModule" class="lg:col-span-2" />
      <SupportCallout v-else :class="hasNarrowCallout ? 'lg:col-span-2' : 'lg:col-span-3'" />

      <ActionCallout v-if="!hasExternalPosOnlySubscription" />
      <SupportCallout v-else-if="firstModule" />
    </div>

    <!-- Rankings -->
    <div v-if="roadmapPerformance" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <StoreRetributionsRanking :sellersWithRetributionRanking="sellerRanking" :showStoreSalesChannel="false" />
      <StoreRankingDetails :storeRanking="storeRanking" />
    </div>

    <!-- Edit target modal -->
    <Modal :open="editTargetModalOpen" @close="editTargetModalOpen = false">
      <StoreEditMonthlySalesTargetForm :store="selectedStoreWithMonthlySalesTarget" @updated="monthlyTargetUpdated" />
    </Modal>

    <!-- Pending claims + Tip -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      <PendingClaimsBlock />
      <DailyTipBlock v-if="quote" :quote="quote" />
    </div>
  </div>
</template>

<script>
import ActionCallout from "@/components/home/ActionCallout.vue";
import QuizCallout from "@/components/home/QuizCallout.vue";
import SupportCallout from "@/components/home/SupportCallout.vue";
import BriefBar from "@/components/home/BriefBar.vue";
import ChallengeHomeBlock from "@/components/home/ChallengeHomeBlock.vue";
import PendingClaimsBlock from "@/components/home/PendingClaimsBlock.vue";
import DailyTipBlock from "@/components/home/DailyTipBlock.vue";
import StoreRetributionsRanking from "@/components/store/StoreRetributionsRanking.vue";
import StoreRankingDetails from "@/components/roadmap/StoreRankingDetails.vue";
import StoreEditMonthlySalesTargetForm from "@/components/store/StoreEditMonthlySalesTargetForm.vue";
import AnalyticsService from "@/api/services/analytics";
import { Loading, Modal, SecondaryButton, toCamelCase } from "@estaly/ui";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  components: {
    ActionCallout,
    QuizCallout,
    SupportCallout,
    BriefBar,
    ChallengeHomeBlock,
    PendingClaimsBlock,
    DailyTipBlock,
    StoreRetributionsRanking,
    StoreRankingDetails,
    StoreEditMonthlySalesTargetForm,
    Loading,
    Modal,
    SecondaryButton,
  },
  data: () => ({
    selectedStoreLocalId: null,
    roadmapPerformance: null,
    quote: null,
    dailyTarget: null,
    yesterdayPerformance: null,
    monthlyPerformance: null,
    sellerRanking: null,
    storeRanking: null,
    leadsWaitingForConfirmation: 0,
    editTargetModalOpen: false,
    // idle (no store to fetch for) | loading | loaded | failed
    trainingModulesStatus: "idle",
  }),
  computed: {
    ...mapGetters("store", ["selectedStoreId", "stores", "selectedStore", "retributionsBoostEnabled"]),
    ...mapGetters("auth", ["hasAccessToAdminDashboard", "isResponsible"]),
    ...mapGetters("businessVerticals", ["hasExternalPosOnlySubscription", "hasSeatsOrFurniture"]),
    ...mapState("trainingModules", ["trainingModules"]),
    firstModule() {
      // Outside of "loaded", the store may still hold the modules of another store or page
      if (this.trainingModulesStatus !== "loaded") return null;
      return this.trainingModules?.[0] || null;
    },
    // The narrow slot holds the subscription, or the support next to the training of an external POS
    hasNarrowCallout() {
      return !this.hasExternalPosOnlySubscription || Boolean(this.firstModule);
    },
    storeOptions() {
      return this.stores
        .filter((s) => s.source === "offline")
        .map((s) => ({
          value: s.id,
          label: s.salesChannel,
        }));
    },
    storeName() {
      return this.selectedStore?.salesChannel ?? "";
    },
    monthlySalesTarget() {
      return this.monthlyPerformance?.target;
    },
    selectedStoreWithMonthlySalesTarget() {
      return {
        ...this.selectedStore,
        monthlySalesTarget: this.monthlySalesTarget || 0,
      };
    },
    allowedToAdjustMonthTarget() {
      return this.monthlySalesTarget > 0 && (this.isResponsible || this.hasAccessToAdminDashboard);
    },
    dataSource() {
      return this.hasSeatsOrFurniture ? "gross_sales" : "plan_sales";
    },
  },
  created() {
    // Before the first render, so that the callouts row is not rendered in a provisional layout
    if (this.selectedStoreId) {
      this.fetchTrainingModules(this.selectedStoreId);
    }
  },
  mounted() {
    if (!this.selectedStoreLocalId && this.storeOptions.length === 1) {
      this.selectedStoreLocalId = this.storeOptions[0].value;
      this.fetchRoadmapPerformance();
    } else if (!this.selectedStoreLocalId && this.selectedStoreId && this.selectedStoreId !== 0) {
      this.selectedStoreLocalId = this.selectedStoreId;
      this.fetchRoadmapPerformance();
    }
  },
  methods: {
    ...mapActions("trainingModules", { fetchTrainingModulesIndex: "fetchIndex" }),
    async fetchRoadmapPerformance() {
      const storeIds = this.selectedStoreLocalId ? [this.selectedStoreLocalId] : [];
      const response = await AnalyticsService.getRoadmapPerformance(storeIds, this.dataSource);
      if (!response?.success) return;
      const data = toCamelCase(response.data);
      this.roadmapPerformance = data;
      this.quote = data.quote;
      this.dailyTarget = data.dailyTarget;
      this.yesterdayPerformance = data.yesterdayPerformance;
      this.monthlyPerformance = data.monthlyPerformance;
      this.sellerRanking = data.sellerRanking;
      this.storeRanking = data.storeRanking;
      this.leadsWaitingForConfirmation = data.leadsWaitingForConfirmation ?? 0;
    },
    async fetchTrainingModules(storeId) {
      this.trainingModulesStatus = "loading";
      const response = await this.fetchTrainingModulesIndex([storeId]);
      // Ignore a response superseded by a more recent fetch, or of a store that is no longer selected
      if (response?.stale || storeId !== this.selectedStoreId) return;
      this.trainingModulesStatus = response?.success ? "loaded" : "failed";
    },
    async monthlyTargetUpdated() {
      this.editTargetModalOpen = false;
      await this.fetchRoadmapPerformance();
    },
  },
  watch: {
    storeOptions(newOptions) {
      if (newOptions.length === 1) {
        this.selectedStoreLocalId = newOptions[0].value;
        this.fetchRoadmapPerformance();
      }
    },
    selectedStoreId(newId) {
      if (newId && newId !== 0 && newId !== this.selectedStoreLocalId) {
        this.selectedStoreLocalId = newId;
        this.fetchRoadmapPerformance();
      }
      if (newId && newId !== 0) {
        this.fetchTrainingModules(newId);
      } else {
        this.trainingModulesStatus = "idle";
      }
    },
  },
};
</script>
